// Beneficiary Claims: a separate persona (claimants, usually after a
// member's death) that shares this bundle with the member journeys.
//
// HOW THE BUNDLE KNOWS. Same mechanism as detectFlow() in ./flow.ts: the
// client ID is injected at build time from the beneficiary_claims_client_id
// Terraform output (acul/.env.production). That was decision 3 in
// INVESTIGATION-beneficiary-claims.md: it needs NO change to any member
// renderer's context_configuration.
//
// Where it matters:
//   * signup-id: ACUL tenant-wide, so members and beneficiaries get the SAME
//     screen and this branch picks the beneficiary form.
//   * login-id / login-passwordless-email-code: ACUL only for the
//     beneficiary client (Terraform renderer filter on client metadata
//     beneficiary_app = "true"), so members keep the standard hosted screens.
//
// Unset ID (a local build before the client exists) means "never
// beneficiary", so a misbuild can't put a member into the claims journey.
declare const __BENEFICIARY_CLIENT_ID__: string;

const BENEFICIARY_CLIENT_ID: string | undefined =
  typeof __BENEFICIARY_CLIENT_ID__ === "string" &&
  __BENEFICIARY_CLIENT_ID__.length > 0
    ? __BENEFICIARY_CLIENT_ID__
    : undefined;

export function isBeneficiaryClient(
  clientId: string | undefined | null
): boolean {
  return (
    !!BENEFICIARY_CLIENT_ID && !!clientId && clientId === BENEFICIARY_CLIENT_ID
  );
}

// AU mobiles only, normalised to E.164. Must stay byte-for-byte equivalent to
// api/src/lib/phone.js normalizeAuMobile() and the toE164 in the Relay and
// Beneficiary Profile Actions. Returns null when it can't be confidently
// normalised.
export function normalizeAuMobile(phone: unknown): string | null {
  if (typeof phone !== "string") return null;
  const digits = phone.replace(/[^\d+]/g, "");

  if (/^\+614\d{8}$/.test(digits)) return digits;
  if (/^614\d{8}$/.test(digits)) return `+${digits}`;
  if (/^04\d{8}$/.test(digits)) return `+61${digits.slice(1)}`;

  return null;
}

// ── Carrying the sign-up details to the code screen ─────────────────────
//
// Passwordless creates the user when the CODE is verified, and Auth0's docs
// put passwordless sign-up field data on the post-login trigger. Rather than
// rely on fields posted on signup-id surviving two screens, they ride along
// again on the code submit, which is the request that creates the user. The
// Beneficiary Profile Action reads them from event.request.body.
//
// sessionStorage, not localStorage: per-tab, gone when the tab closes, and
// signup-id and the code screen share the auth.azuremark.co origin in the
// same tab. The email is NOT stored: Auth0 already holds it as the
// identifier. TTL-stamped so a stale entry from an abandoned sign-up can't
// attach itself to a later, unrelated login in the same tab.
const STORAGE_KEY = "caresuper.beneficiarySignup";
const TTL_MS = 30 * 60 * 1000;

export interface BeneficiarySignupDetails {
  givenName: string;
  familyName: string;
  mobile: string; // E.164
}

export const ULP_FIELDS = {
  givenName: "ulp-given-name",
  familyName: "ulp-family-name",
  mobile: "ulp-mobile",
} as const;

export function saveSignupDetails(details: BeneficiarySignupDetails): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...details, savedAt: Date.now() })
    );
  } catch {
    // Storage blocked: the fields still went on the signup-id POST, and the
    // app's "complete your details" step is the safety net.
  }
}

export function readSignupDetails(): BeneficiarySignupDetails | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.savedAt !== "number" ||
      Date.now() - parsed.savedAt > TTL_MS
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const mobile = normalizeAuMobile(parsed.mobile);
    if (
      typeof parsed.givenName !== "string" ||
      typeof parsed.familyName !== "string" ||
      !mobile
    ) {
      return null;
    }
    return {
      givenName: parsed.givenName,
      familyName: parsed.familyName,
      mobile,
    };
  } catch {
    return null;
  }
}

// The extra keys for submitCode()/signup(). Empty when nothing is carried,
// e.g. a returning beneficiary logging in.
export function signupDetailsAsUlpFields(
  details: BeneficiarySignupDetails | null
): Record<string, string> {
  if (!details) return {};
  return {
    [ULP_FIELDS.givenName]: details.givenName,
    [ULP_FIELDS.familyName]: details.familyName,
    [ULP_FIELDS.mobile]: details.mobile,
  };
}
